import {customElement, property, state} from "lit/decorators.js";
import {html, type TemplateResult} from "lit";
import {UnLitElement} from "../../../common/elements";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {TemplateModel} from "../../../common/template";
import {LocatedDates} from "../../../common/utils/dates/locatedDates";
import {resolve} from "../../../container";
import {CONFIRMABLE_CHANNELS, ICONS, TAG_LIMIT} from "../feedCommons";
import type {FeedEntry, FeedEntryData} from "../feedEntry";
import {ifDefined} from "lit/directives/if-defined.js";
import type {Maybe, Nullable} from "../../../bootstrap/common/types";
import {IMAGE_TEASER_SIZE} from "../../../page/elements/teaser/imageTeaser";
import {Resolution} from "../../../common/resolution";
import {BOOTSTRAP} from "../../../common/resolutionConstants";
import {trackingTeaserInteract} from "../../../tracking/trackingAttributes";

enum ContentTypeClass {
    PAGE = "page",
    SOCIAL = "social-media",
    YOUTUBE = "youtube",
    PRESS = "press"
}

export type InformationDensityClass = "" | "no-tags" | "minimal";
export type CardBoxingClass = "card-style-boxed" | "card-style-transparent" | "card-style-image-teaser";

export type FeedTileConfig = {
    arrangement: string;
    informationDensityClass: InformationDensityClass;
    cardBoxingClass: CardBoxingClass;
    usePageOverlay: boolean;
    contextPreservingPageOverlay: boolean;
    topicsLinked: boolean;
}

@customElement("eop-feed-tile")
export class EopFeedTile extends UnLitElement {

    @property()
    public entry: FeedEntry;
    @property()
    public config: FeedTileConfig;
    @state()
    private boxed: boolean = false;

    private data: FeedEntryData;

    public constructor(
        private dates: LocatedDates = resolve(LocatedDates),
        private resolution: Resolution = resolve(Resolution)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.resolution.onWindowResize(() => this.updatedBoxed());
        this.updatedBoxed();
        this.data = this.entry.data;
    }

    private updatedBoxed(): void {
        this.boxed = this.isBoxed();
    }

    private isBoxed(): boolean {
        const isMobile = this.resolution.upTo(BOOTSTRAP.SM);
        const isCondensed = this.config.arrangement === "mobile-condensed";
        const isTransparent = this.config.cardBoxingClass === "card-style-transparent";
        return !(isTransparent || (isMobile && isCondensed));
    }

    public render(): TemplateResult {
        return this.needsConfirmation()
            ? this.renderConfirmableTile()
            : this.renderTileWithoutConfirmation();
    }

    private needsConfirmation(): boolean {
        return !!this.data.contentType && CONFIRMABLE_CHANNELS.includes(this.data.contentType);
    }

    private renderConfirmableTile(): TemplateResult {
        return html`
            <eop-load-after-confirmation
                    module-name="youtube"
                    confirmation-text-key="YOUTUBE_CONFIRMATION_TEXT"
                    button-label-key="YOUTUBE_CONFIRMATION_BUTTON_LABEL"
            >
                <span eop-slot="content-to-confirm">
                    ${this.renderTileWithoutConfirmation()}
                </span>
            </eop-load-after-confirmation>
        `;
    }

    private renderTileWithoutConfirmation(): TemplateResult {
        return this.config.cardBoxingClass === "card-style-image-teaser"
            ? this.renderImageTeaserTile()
            : this.renderNormalTile();
    }

    private renderNormalTile(forImageTeaser: boolean = false): TemplateResult {
        if (this.config.usePageOverlay) {
            return html`
                <eop-page-overlay-link
                        href=${this.data.url}
                        ${trackingTeaserInteract(this.data.headline ?? "tile", this.data.url)}
                        context-preserving=${ifDefined(this.config.contextPreservingPageOverlay ? "true" : undefined)}
                        class="tile ${this.contentTypeClass()} ${this.config.informationDensityClass} ${this.config.cardBoxingClass} ${this.config.arrangement} ${forImageTeaser ? "for-image-teaser" : ""} ${this.boxed ? "reverse-background" : ""}"
                >
                    ${this.renderInnerTile()}
                </eop-page-overlay-link>
            `;
        }
        return html`
            <a href=${this.data.url}
               ${trackingTeaserInteract(this.data.headline ?? "tile", this.data.url)}
               target=${ifDefined(this.entry.isExternalLink() ? "_blank" : undefined)}
               class="tile ${this.contentTypeClass()} ${this.config.informationDensityClass} ${this.config.cardBoxingClass} ${this.config.arrangement} ${forImageTeaser ? "for-image-teaser" : ""} ${this.boxed ? "reverse-background" : ""}"
            >
                ${this.renderInnerTile()}
            </a>
        `;
    }

    private renderInnerTile(): TemplateResult {
        return html`
            <div class="tile-image">
                ${this.imageElement()}
            </div>
            <div class="tile-text">
                <div class="tile-top">
                    <div class="metadata">${this.metaData()}</div>
                </div>
                ${this.renderHeadline()}
                <div class="tile-description">
                    <span class="description-text">${this.data.subHeadline ? unsafeHTML(this.data.subHeadline) : ""}</span>
                    ${this.renderTopics()}
                </div>
                ${this.iconElement()}
            </div>
        `;
    }

    private renderImageTeaserTile(): TemplateResult {
        return html`
            ${this.config.arrangement === "mobile-condensed" ? this.renderNormalTile(true) : ""}
            <eop-image-teaser
                    link=${this.data.url}
                    headline=${this.data.headline}
                    target=${ifDefined(this.entry.isExternalLink() ? "_blank" : undefined)}
                    hover-text=${this.data.imageAltText}
                    image-src=${this.entry.getImageUrlFor4x3Purpose()}
                    image-size=${IMAGE_TEASER_SIZE.SMALL}
                    ?open-in-page-overlay=${this.config.usePageOverlay}
                    class=${this.config.arrangement}
                    .tags=${this.config.informationDensityClass === "" ? this.renderTopics() : ""}>
            </eop-image-teaser>
        `;
    }

    private renderHeadline(): Nullable<TemplateResult> {
        return this.data.headline
            ? html`
                    <div class="tile-headline">${unsafeHTML(this.data.headline)}</div>`
            : null;
    }

    private renderTopics(): TemplateResult {
        return html`
            <div class="tags">${(this.renderTopicTags())}
            </div>
        `;
    }


    private renderTopicTags(): TemplateResult[] {
        return this.data.topics
            .filter(it => this.config.topicsLinked ? !!it.url : it)
            .slice(0, TAG_LIMIT)
            .map(topic => html`
                <eop-feed-topic-tag ?linked=${this.config.topicsLinked} .topic=${topic}></eop-feed-topic-tag>`);
    }

    private imageElement(): TemplateResult {
        if (this.data.mediaType === "image") {
            return html`
                <eop-plain-image
                        src=${this.entry.getImageUrlFor7x4Purpose()}
                        alt=${this.data.imageAltText}
                        class="eop-image"
                ></eop-plain-image>
            `;
        } else if (this.data.contentType === "youtube") {
            return html`
                <eop-confirmable-iframe-element
                        src=${this.data.url}
                        module-name="youtube"
                        frame-title=${this.data.headline}
                ></eop-confirmable-iframe-element>`;
        } else {
            return html`
                <eop-responsive-image
                        image-src=${this.entry.getImageUrlFor7x4Purpose()}
                        image-alt=${this.data.imageAltText}
                ></eop-responsive-image>`;
        }
    }

    private iconElement(): Maybe<Element | Text> {
        const icon = this.data.contentType ? ICONS[this.data.contentType] : null;
        if (!icon) {
            return document.createTextNode("");
        }
        return new TemplateModel(icon)
            .addClasses("feed-type-icon")
            .asElement();
    }

    private metaData(): string {
        const date = this.dates.toLocalDateString(new Date(this.data.modifiedDate));
        const contentTypeDescription = this.entry.pageTypeDescription();
        if (contentTypeDescription) {
            return `${date} | ${contentTypeDescription}`;
        }
        return `${date}`;
    }

    private contentTypeClass(): ContentTypeClass {
        switch (this.data.contentType) {
            case "twitter":
            case "facebook":
            case "instagram":
            case "linkedin":
                return ContentTypeClass.SOCIAL;
            case "youtube":
                return ContentTypeClass.YOUTUBE;
            case "press":
                return ContentTypeClass.PRESS;
            default:
                return ContentTypeClass.PAGE;
        }
    }
}